import { useCallback, useRef, useState } from "react";
import {
  ArrowSquareOut,
  ArrowsClockwise,
  CaretDown,
  Clock,
  Envelope,
  FileText,
  Hash,
  Info,
  MagnifyingGlass,
  MapPin,
  Pencil,
  Phone,
  Receipt,
  Sparkle,
  Tag,
  User,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { setProjectHours } from "../../../api/projects";
import { getStatusConfig, PROJECT_STATUSES } from "../../../data/mockProjects";
import { TrainingLogCard } from "./ProjectDetailActivitySections";
import { fmtDate } from "./formatters";

export function StatusDropdown({ status, onSave }) {
  const [saving, setSaving] = useState(false);
  const cfg = getStatusConfig(status);

  return (
    <div className="relative inline-flex items-center">
      <select
        value={status}
        disabled={saving}
        data-testid="status-dropdown"
        onChange={async (e) => {
          setSaving(true);
          try {
            await onSave("status", e.target.value);
          } finally {
            setSaving(false);
          }
        }}
        className={`appearance-none pl-2 pr-6 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm cursor-pointer transition-opacity ${cfg.color} ${saving ? "opacity-50" : ""}`}
      >
        {PROJECT_STATUSES.map((projectStatus) => (
          <option key={projectStatus.value} value={projectStatus.value}>{projectStatus.label}</option>
        ))}
      </select>
      <CaretDown size={9} className="absolute right-1.5 pointer-events-none opacity-60" />
    </div>
  );
}

function EditableInfoRow({ icon: Icon, label, value, field, placeholder = "Click to add", onSave }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  const start = () => {
    setEditValue(value || "");
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    onSave(field, editValue.trim() || null);
  };

  const cancel = () => setEditing(false);

  if (editing) {
    return (
      <div className="flex items-start gap-3 py-2.5 border-b border-slate-50 last:border-0">
        <Icon size={14} className="text-slate-400 flex-shrink-0 mt-1.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
          <input
            autoFocus
            className="w-full text-sm border border-slate-300 rounded px-2 py-0.5 outline-none focus:border-primary"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") cancel(); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-50 last:border-0 group cursor-pointer" onClick={start} data-testid={`editable-${field}`}>
      <Icon size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
          {value ? <p className="text-sm text-slate-800 mt-0.5 break-words">{value}</p> : <p className="text-sm text-slate-300 mt-0.5 italic">{placeholder}</p>}
        </div>
        <Pencil size={11} className="text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
      </div>
    </div>
  );
}

function EditableHoursRow({ project, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const hours = project.estimated_hours;
  const isManual = project.estimated_hours_manual;
  const reasoning = project.estimated_hours_reasoning;

  const start = () => {
    setEditValue(hours != null ? String(hours) : "");
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  const commit = async () => {
    const nextValue = parseFloat(editValue);
    if (!Number.isFinite(nextValue) || nextValue < 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const updated = await setProjectHours(project.id, nextValue);
      onUpdate(updated);
    } catch (e) {
      toast.error(`Failed to save hours: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-start gap-3 py-2.5 border-b border-slate-50 last:border-0">
        <Clock size={14} className="text-slate-400 flex-shrink-0 mt-1.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Hours Estimate</p>
          <div className="flex items-center gap-2">
            <input
              autoFocus
              type="number"
              step="0.5"
              min="0"
              className="w-28 text-sm border border-slate-300 rounded px-2 py-0.5 outline-none focus:border-primary"
              value={editValue}
              disabled={saving}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") cancel(); }}
              data-testid="hours-estimate-input"
            />
            <span className="text-xs text-slate-400">hrs</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-50 last:border-0 group cursor-pointer" onClick={start} data-testid="editable-estimated_hours">
      <Clock size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Hours Estimate</p>
          {hours != null ? (
            <>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-sm text-slate-800">{Number(hours).toFixed(1)} hrs</span>
                {isManual ? null : (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-50 border border-blue-100 text-blue-600 text-[9px] font-semibold rounded">
                    <Sparkle size={8} weight="fill" /> AI
                  </span>
                )}
              </div>
              {!isManual && reasoning && <p className="text-[10px] text-slate-400 italic mt-0.5 leading-tight line-clamp-2">{reasoning}</p>}
            </>
          ) : (
            <p className="text-sm text-slate-300 mt-0.5 italic">Click to add</p>
          )}
        </div>
        <Pencil size={11} className="text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
      </div>
    </div>
  );
}

function EditableLocationsRow({ project, onSave }) {
  const locations = Array.isArray(project.project_location)
    ? project.project_location
    : (project.project_location ? [project.project_location] : []);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  const save = useCallback((next) => onSave("project_location", next), [onSave]);

  const remove = (index) => {
    const next = locations.filter((_, candidateIndex) => candidateIndex !== index);
    save(next);
  };

  const add = () => {
    const value = draft.trim();
    if (value && !locations.includes(value)) save([...locations, value]);
    setDraft("");
  };

  const handleKey = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      add();
    }
    if (event.key === "Escape") {
      setDraft("");
      setEditing(false);
    }
  };

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-50 last:border-0" data-testid="editable-project_location">
      <MapPin size={14} className="text-slate-400 flex-shrink-0 mt-1" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Location</p>
        <div className="flex flex-wrap gap-1 items-center">
          {locations.map((location, index) => (
            <span key={index} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded">
              {location}
              <button type="button" onClick={() => remove(index)} className="text-slate-400 hover:text-slate-700 ml-0.5">
                <X size={10} weight="bold" />
              </button>
            </span>
          ))}
          {editing ? (
            <input
              ref={inputRef}
              autoFocus
              type="text"
              className="text-xs border border-slate-300 rounded px-2 py-0.5 outline-none focus:border-primary w-36"
              placeholder="Add location…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKey}
              onBlur={() => { add(); setEditing(false); }}
              data-testid="location-add-input"
            />
          ) : (
            <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-0.5 text-xs text-slate-400 hover:text-slate-600 transition-colors" data-testid="location-add-btn">
              <MapPin size={10} />
              {locations.length === 0 ? "Add location" : "Add"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-50 last:border-0">
      <Icon size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm text-[#0078D4] hover:underline flex items-center gap-1 mt-0.5">
            {value} <ArrowSquareOut size={11} />
          </a>
        ) : (
          <p className="text-sm text-slate-800 mt-0.5 break-words">{value}</p>
        )}
      </div>
    </div>
  );
}

export function ProjectDetailSidebar({
  project,
  maxEmails,
  msConnected,
  onFieldSave,
  onProjectUpdate,
  onTriggerBackfill,
}) {
  return (
    <aside className="space-y-6 min-w-0">
      <section className="bg-white border border-slate-200 rounded-sm p-5">
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Details</h3>
        <EditableLocationsRow project={project} onSave={onFieldSave} />
        <EditableInfoRow icon={Receipt} label="Invoice Number" value={project.invoice_number} field="invoice_number" onSave={onFieldSave} />
        <EditableInfoRow icon={Hash} label="Purchase Order" value={project.po_number} field="po_number" onSave={onFieldSave} />
        <EditableHoursRow project={project} onUpdate={onProjectUpdate} />
        <InfoRow icon={User} label="Primary Contact" value={project.primary_contact} />
        <InfoRow icon={Phone} label="Phone" value={project.phone} />
        <InfoRow icon={FileText} label="Created" value={fmtDate(project.created_at)} />
        <InfoRow icon={FileText} label="Last Updated" value={fmtDate(project.updated_at)} />
      </section>

      {(project.clients?.length > 0 || project.client_emails?.length > 0) && (
        <section className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Clients</h3>
          {project.clients?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {project.clients.map((client) => (
                <span key={client} className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-sm text-xs font-medium text-slate-700">
                  <User size={10} />{client}
                </span>
              ))}
            </div>
          )}
          {project.client_emails?.length > 0 && (
            <div className="flex flex-col gap-1">
              {project.client_emails.map((email) => (
                <a key={email} href={`mailto:${email}`} className="text-xs text-[#0078D4] hover:underline flex items-center gap-1.5">
                  <Envelope size={12} />{email}
                </a>
              ))}
            </div>
          )}

          {project.client_emails?.length > 0 && msConnected && project.onedrive_folder_id && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <button
                onClick={onTriggerBackfill}
                disabled={project.backfill_status === "running"}
                data-testid="scan-inbox-client-btn"
                title={
                  `Searches your Outlook inbox for the last ${maxEmails} emails from each address listed above.\n\n` +
                  "• AI scores every email for relevance to this project\n" +
                  "• Matching emails are saved to the project's OneDrive folder\n" +
                  "• Attachments are classified and saved to drawings/ or emails/\n" +
                  "• Emails are processed oldest-first so drawing versions accumulate correctly\n" +
                  "• Already-processed emails are automatically skipped"
                }
                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {project.backfill_status === "running" ? <ArrowsClockwise size={11} className="animate-spin flex-shrink-0" /> : <MagnifyingGlass size={11} className="flex-shrink-0" />}
                <span className="flex-1 text-left">{project.backfill_status === "running" ? "Scanning inbox…" : "Scan inbox for these emails"}</span>
              </button>
              <p className="text-[10px] text-slate-400 mt-1.5 flex items-start gap-1 leading-relaxed">
                <Info size={10} className="flex-shrink-0 mt-px" />
                Searches the last {maxEmails} emails from each address above and routes matching ones through the email automation. Hover for details.
              </p>
            </div>
          )}
        </section>
      )}

      <TrainingLogCard projectId={project.id} />

      {project.tags?.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Tags</h3>
          <div className="flex flex-wrap gap-1.5">
            {project.tags.map((tag) => (
              <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/5 border border-primary/20 rounded-sm text-xs text-primary">
                <Tag size={9} />{tag}
              </span>
            ))}
          </div>
        </section>
      )}

      {project.notes && (
        <section className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Notes</h3>
          <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">{project.notes}</p>
        </section>
      )}
    </aside>
  );
}
